import type { LucideIcon } from 'lucide-react';
import {
  CreditCard,
  Shield,
  FileText,
  Video,
  MessageCircle,
  Wrench,
  TrendingUp,
  Search,
  Users,
  Package,
  Settings,
  Gauge,
  Handshake,
  HandCoins,
} from 'lucide-react';

import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

export interface ServiceCatalogEntry {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  image: string;
}

export const SERVICE_CATALOG: Record<string, ServiceCatalogEntry> = {
  '65da42048eca108d87c91da6': {
    icon: Video,
    title: 'Agendar Videochamada',
    description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa',
    color: 'bg-blue-500',
    image: videoCall,
  },
  '65dcb7548eca108d87c91dbb': {
    icon: Search,
    title: 'Avaliação',
    description: 'Avaliação profissional do seu veículo com preço justo de mercado',
    color: 'bg-green-500',
    image: valuation,
  },
  '65dcb7c58eca108d87c91dbd': {
    icon: HandCoins,
    title: 'Consignação',
    description: 'Deixe seu veículo em consignação conosco e venda com segurança',
    color: 'bg-purple-500',
    image: consignment,
  },
  '65dcef468eca108d87c91dc8': {
    icon: Users,
    title: 'Consórcio',
    description: 'Adquira seu veículo através de consórcio com as melhores condições',
    color: 'bg-teal-500',
    image: consortium,
  },
  '65dcb6528eca108d87c91db9': {
    icon: FileText,
    title: 'Despachante',
    description: 'Serviços de despachante para documentação veicular completa',
    color: 'bg-orange-500',
    image: forwardingAgent,
  },
  '65dcb7898eca108d87c91dbc': {
    icon: CreditCard,
    title: 'Financiamento',
    description: 'Análise de crédito e financiamento com as melhores taxas do mercado',
    color: 'bg-blue-600',
    image: financing,
  },
  '6628fd535bda2ae51f6fc5ff': {
    icon: Package,
    title: 'Peças e Acessórios',
    description: 'Peças originais e acessórios para seu veículo',
    color: 'bg-gray-600',
    image: partsAccessories,
  },
  '65dcef098eca108d87c91dc7': {
    icon: Wrench,
    title: 'Revisões',
    description: 'Revisões preventivas e corretivas com equipe especializada',
    color: 'bg-slate-600',
    image: scheduleReview,
  },
  '65dcb7238eca108d87c91dba': {
    icon: Shield,
    title: 'Seguros',
    description: 'Cotação de seguros com as principais seguradoras do país',
    color: 'bg-red-500',
    image: insurance,
  },
  '65dceeac8eca108d87c91dc6': {
    icon: Settings,
    title: 'Serviços',
    description: 'Serviços automotivos especializados para seu veículo',
    color: 'bg-indigo-600',
    image: scheduleServices,
  },
  '65dcb7fe8eca108d87c91dbe': {
    icon: TrendingUp,
    title: 'Tabela FIPE',
    description: 'Consulte o valor do seu veículo pela tabela FIPE',
    color: 'bg-cyan-500',
    image: fipeTable,
  },
  '65dcb5e78eca108d87c91db8': {
    icon: Gauge,
    title: 'Test-Drive',
    description: 'Agende um test drive e experimente o veículo dos seus sonhos',
    color: 'bg-indigo-500',
    image: testdrive,
  },
  '65dcb8548eca108d87c91dbf': {
    icon: Handshake,
    title: 'Vendas Diretas',
    description: 'Vendas diretas com as melhores condições e atendimento personalizado',
    color: 'bg-green-600',
    image: directSales,
  },
};

const DEFAULT_SERVICE_INFO: ServiceCatalogEntry = {
  icon: MessageCircle,
  title: 'Serviço',
  description: 'Entre em contato para mais informações sobre este serviço',
  color: 'bg-gray-500',
  image: '',
};

const SERVICE_BUTTON_TEXTS: Record<string, string> = {
  '65da42048eca108d87c91da6': 'Agendar',
  '65dceeac8eca108d87c91dc6': 'Agendar',
  '65dcef098eca108d87c91dc7': 'Agendar',
  '65dcb5e78eca108d87c91db8': 'Agendar',
  '65dcb7898eca108d87c91dbc': 'Simular',
  '65dcb7548eca108d87c91dbb': 'Avaliar',
  '65dcb7c58eca108d87c91dbd': 'Avaliar',
  '65dcb7238eca108d87c91dba': 'Cotar',
  '65dcb6528eca108d87c91db9': 'Cotar',
  '6628fd535bda2ae51f6fc5ff': 'Cotar',
  '65dcef468eca108d87c91dc8': 'Solicitar',
  '65dcb8548eca108d87c91dbf': 'Solicitar',
  '65dcb7fe8eca108d87c91dbe': 'Consultar',
};

export function getServiceInfo(serviceId: string): ServiceCatalogEntry {
  return SERVICE_CATALOG[serviceId] ?? DEFAULT_SERVICE_INFO;
}

export function getServiceButtonText(serviceId: string): string {
  return SERVICE_BUTTON_TEXTS[serviceId] ?? 'Solicitar';
}

export function getServiceImage(service: { image?: string }, info: ServiceCatalogEntry): string {
  return service.image || info.image;
}
